import { useCallback, useEffect } from 'react';

interface UseKeyboardShortcutOptions {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  enabled?: boolean;
}

/**
 * Custom hook for handling keyboard shortcuts
 * @param options - Configuration for the keyboard shortcut
 * @param callback - Function to call when the shortcut is triggered
 */
export function useKeyboardShortcut(options: UseKeyboardShortcutOptions, callback: () => void) {
  const { key, ctrlKey = false, shiftKey = false, altKey = false, enabled = true } = options;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Check if the key matches
      if (event.key.toLowerCase() !== key.toLowerCase()) return;

      // Check modifier keys
      const hasCtrlOrMeta = event.ctrlKey || event.metaKey;
      if (ctrlKey && !hasCtrlOrMeta) return;
      if (!ctrlKey && hasCtrlOrMeta) return;
      if (event.shiftKey !== shiftKey) return;
      if (event.altKey !== altKey) return;

      // Check if focus is on an input element (to avoid conflicts)
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          (activeElement as HTMLElement).contentEditable === 'true')
      ) {
        return;
      }

      // Prevent default behavior and call the callback
      event.preventDefault();
      event.stopPropagation();
      callback();
    },
    [key, ctrlKey, shiftKey, altKey, enabled, callback],
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, enabled]);
}

/**
 * Hook specifically for chat sidebar toggle shortcut (Cmd/Ctrl + K)
 * @param onToggle - Function to call when the shortcut is triggered
 * @param enabled - Whether the shortcut is enabled
 */
export function useChatSidebarShortcut(onToggle: () => void, enabled = true) {
  useKeyboardShortcut(
    {
      key: 'k',
      ctrlKey: true, // This will match either Ctrl or Cmd due to the logic above
      enabled,
    },
    onToggle,
  );
}
